/**
 * Feature flags per organizacja — Sprint 40.
 *
 * Każdy klucz reprezentuje moduł który SUPER_ADMIN może włączyć/wyłączyć
 * per organizacja. Domyślnie moduł jest wyłączony gdy brak wpisu w
 * `Organization.enabledModules`. SUPER_ADMIN zawsze ma dostęp niezależnie
 * od flagi (pełen widok ponad organizacjami).
 *
 * Dodawanie nowego modułu:
 *   1. Dodaj klucz do `MODULE_KEYS` + wpis w `MODULE_METADATA`
 *   2. Wywołaj `assertModuleEnabled(user, 'nowyModul')` w service/controller
 *   3. Frontend: `useModuleEnabled('nowyModul')` do ukrycia UI
 */

export const MODULE_KEYS = ['cad', 'aiPhotoAnalysis', 'markerStats'] as const;

export type ModuleKey = (typeof MODULE_KEYS)[number];

export interface ModuleMetadata {
  key: ModuleKey;
  label: string;
  description: string;
}

export const MODULE_METADATA: Record<ModuleKey, ModuleMetadata> = {
  cad: {
    key: 'cad',
    label: 'CAD / DWG',
    description:
      'Viewer rysunków technicznych DWG/DXF z markerami i review comments. Dla organizacji pracujących z dokumentacją techniczną (przemysł, infrastruktura, architektura).',
  },
  aiPhotoAnalysis: {
    key: 'aiPhotoAnalysis',
    label: 'Analiza zdjęć AI',
    description:
      'Propozycja opisu i typu uszkodzenia dla zdjęć markera, generowana przez zewnętrzny model. Zdjęcia opuszczają infrastrukturę Pinveo — moduł wymaga świadomej zgody organizacji.',
  },
  markerStats: {
    key: 'markerStats',
    label: 'Statystyki markerów',
    description:
      'Rozkład wartości w polach wyboru markera — na obiekcie, na projekcie i jako filtr listy. Nazwy wymiarów pochodzą z etykiet pól skonfigurowanych przez organizację.',
  },
};

export function isValidModuleKey(value: string): value is ModuleKey {
  return (MODULE_KEYS as readonly string[]).includes(value);
}

export type EnabledModulesMap = Partial<Record<ModuleKey, boolean>>;

/**
 * Pure-function wariant — frontend i backend używają tej samej logiki.
 * Użyj `ModuleAccessService` gdy potrzebujesz guarda z NestJS DI.
 */
export function checkModuleEnabled(
  userRole: string,
  enabledModules: EnabledModulesMap | undefined | null,
  moduleKey: ModuleKey,
): boolean {
  if (userRole === 'SUPER_ADMIN') return true;
  if (!enabledModules) return false;
  return enabledModules[moduleKey] === true;
}

/**
 * Zgoda organizacji na moduł — **celowo bez parametru roli**.
 *
 * `checkModuleEnabled` wyżej przepuszcza SUPER_ADMINa niezależnie od flagi
 * i tak ma zostać: dla modułów czysto funkcjonalnych (np. `cad`) to wygodny
 * pełny widok ponad organizacjami. Są jednak flagi, które nie są przełącznikiem
 * funkcji, tylko **zgodą na wyniesienie danych klienta poza infrastrukturę** —
 * dziś `aiPhotoAnalysis` (zdjęcia jadą do zewnętrznego dostawcy modelu).
 * Zgoda, którą obchodzi rola, nie jest zgodą, więc ta funkcja nie dostaje roli
 * do ręki i nie ma jak jej uwzględnić.
 *
 * Używają jej obie strony: `assertConsent` w
 * `apps/api/src/ai/ai-proposal.service.ts` (blokada) i `useModuleConsent`
 * w `apps/web/src/lib/auth.tsx` (ukrycie przycisku).
 */
export function checkModuleConsent(
  enabledModules: EnabledModulesMap | undefined | null,
  moduleKey: ModuleKey,
): boolean {
  if (!enabledModules) return false;
  return enabledModules[moduleKey] === true;
}

export function checkModuleEnabledWithProject(
  userRole: string,
  orgModules: EnabledModulesMap | undefined | null,
  projectModules: EnabledModulesMap | undefined | null,
  moduleKey: ModuleKey,
): boolean {
  if (userRole === 'SUPER_ADMIN') return true;
  if (!orgModules || orgModules[moduleKey] !== true) return false;
  if (projectModules && projectModules[moduleKey] === false) return false;
  return true;
}
